import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { DateTimeText } from '@/src/components/ui/date-time'
import { Card, CardHeader, CardTitle, CardContent } from '@/src/components/ui/card'
import { UsersIcon, FileTextIcon, FolderSimpleIcon, ImageIcon, CheckCircleIcon, XCircleIcon, WarningCircleIcon, BuildingsIcon, ChartLineUpIcon, TrendUpIcon, ClockIcon, CalendarIcon } from '@phosphor-icons/react'
import { Spinner } from '@/src/components/ui/spinner'
import { getAdminStats, getAppVersion } from '@/src/api/admin'
import { getJobProcessorHealth } from '@/src/api/job'
import AdminToolbar from '@/src/components/layout/AdminToolbar'

export const Route = createFileRoute('/_manage/manage/admin/dashboard/')({
  component: StatisticsPage,
})

function StatCard({ 
  title, 
  value, 
  description, 
  icon: Icon 
}: { 
  title: string
  value: number | string
  description?: string | React.ReactNode
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <div className="text-xs text-muted-foreground mt-1">{description}</div>
        )}
      </CardContent>
    </Card>
  )
}

function RecentActivityCard({ title, items, icon: Icon, type }: { 
  title: string
  items: Array<any>
  icon: React.ComponentType<{ className?: string }>
  type: 'user' | 'file'
}) {
  return (
    <Card className="col-span-full lg:col-span-1">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const user = type === 'file' && item.userFiles?.[0]?.user ? item.userFiles[0].user : null
              return (
                <div key={item.id} className="flex flex-col border-b pb-2 last:border-0">
                  <div className="text-sm font-medium truncate">
                    {item.name || item.email || 'Unknown'}
                  </div>
                  {user && (
                    <div className="text-xs text-muted-foreground truncate">
                      by {user.name || user.email}
                    </div>
                  )}
                        <div className="text-xs text-muted-foreground">
                          <DateTimeText>{item.createdAt}</DateTimeText>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function GrowthCard({ 
  title, 
  weekValue, 
  monthValue, 
  icon: Icon 
}: { 
  title: string
  weekValue: number
  monthValue: number
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Last 7 days</span>
            <span className="text-lg font-bold">{weekValue}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Last 30 days</span>
            <span className="text-lg font-bold">{monthValue}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function StatisticsPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => getAdminStats(),
  })

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['jobProcessorHealth'],
    queryFn: () => getJobProcessorHealth(),
    refetchInterval: (query) => {
      // Stop polling if page is hidden
      if (document.hidden) return false
      // Poll every 60s instead of 30s
      return 60000
    },
  })

  const { data: appVersion } = useQuery({
    queryKey: ['appVersion'],
    queryFn: () => getAppVersion(),
    staleTime: Infinity, // Version doesn't change during runtime
  })

  if (isLoading) {
    return (
      <div className="bg-background text-foreground">
        <AdminToolbar>
          <AdminToolbar.Title className="text-lg font-semibold mb-1 md:mb-0">Admin Dashboard</AdminToolbar.Title>
        </AdminToolbar>
        <Spinner size={48} />
      </div>
    )
  }

  const healthStatus = health?.status ?? 'unknown'
  const HealthIcon = 
    healthStatus === 'healthy' ? CheckCircleIcon :
    healthStatus === 'degraded' ? WarningCircleIcon :
    XCircleIcon

  // Build comprehensive job-processor status description
  const buildJobProcessorDescription = () => {
    if (!health) return 'Loading...'
    
    const parts: string[] = []
    
    // Job status
    parts.push(`${health.activeJobs ?? 0} active, ${health.pendingJobs ?? 0} pending`)
    
    // Redis heartbeat status
    if (health.heartbeatAge !== null) {
      parts.push(`Heartbeat: ${health.heartbeatAge}s ago`)
    } else {
      parts.push('No heartbeat')
    }
    
    // HTTP endpoint status
    if (health.jobProcessorEndpoint || health.executorEndpoint) {
      parts.push('HTTP: connected')
    } else {
      parts.push('HTTP: unreachable')
    }
    
    // Version info
    if (health.jobProcessorVersion || health.executorVersion) {
      parts.push(`v${health.jobProcessorVersion ?? health.executorVersion}`)
    }
    
    return parts.join(' • ')
  }

  const healthDescription = buildJobProcessorDescription()
  
  // Build detailed info line
  const jobProcessorInfo = health ? (
    health.heartbeatAge !== null 
      ? `Redis heartbeat active (${health.heartbeatAge}s ago)${(health.jobProcessorEndpoint || health.executorEndpoint) ? ' • HTTP endpoint reachable' : ' • HTTP endpoint unreachable (normal for isolated networks)'}` 
      : `No Redis heartbeat detected${(health.jobProcessorEndpoint || health.executorEndpoint) ? ' • HTTP endpoint reachable' : ''}`
  ) : 'Not configured'

  return (
    <div className="bg-background text-foreground">
      <div className="space-y-6">
        {/* Main Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <StatCard
            title="Job Processor Status"
            value={healthStatus.charAt(0).toUpperCase() + healthStatus.slice(1)}
            description={
              <div className="space-y-1">
                <div>{healthDescription}</div>
                <div className="text-xs opacity-75 truncate" title={jobProcessorInfo}>
                  {jobProcessorInfo}
                </div>
              </div>
            }
            icon={HealthIcon}
          />
          <StatCard
            title="Total Users"
            value={stats?.userCount ?? 0}
            description={`+${stats?.newUsersThisWeek ?? 0} this week`}
            icon={UsersIcon}
          />
          <StatCard
            title="Total Files"
            value={stats?.fileCount ?? 0}
            description={`+${stats?.newFilesThisWeek ?? 0} this week`}
            icon={FileTextIcon}
          />
          <StatCard
            title="Total Projects"
            value={stats?.projectCount ?? 0}
            description={`+${stats?.newProjectsThisWeek ?? 0} this week`}
            icon={FolderSimpleIcon}
          />
          <StatCard
            title="Total Drawings"
            value={stats?.drawingCount ?? 0}
            description={`+${stats?.newDrawingsThisWeek ?? 0} this week`}
            icon={ImageIcon}
          />
          <StatCard
            title="Organizations"
            value={stats?.organizationCount ?? 0}
            icon={BuildingsIcon}
          />
        </div>

        {/* Growth Metrics */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ChartLineUpIcon className="h-5 w-5" />
            Growth Metrics
          </h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <GrowthCard
              title="New Users"
              weekValue={stats?.newUsersThisWeek ?? 0}
              monthValue={stats?.newUsersThisMonth ?? 0}
              icon={UsersIcon}
            />
            <GrowthCard
              title="New Files"
              weekValue={stats?.newFilesThisWeek ?? 0}
              monthValue={stats?.newFilesThisMonth ?? 0}
              icon={FileTextIcon}
            />
            <GrowthCard
              title="New Projects"
              weekValue={stats?.newProjectsThisWeek ?? 0}
              monthValue={stats?.newProjectsThisMonth ?? 0}
              icon={FolderSimpleIcon}
            />
            <GrowthCard
              title="New Drawings"
              weekValue={stats?.newDrawingsThisWeek ?? 0}
              monthValue={stats?.newDrawingsThisMonth ?? 0}
              icon={ImageIcon}
            />
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5" />
            Recent Activity
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <RecentActivityCard
              title="Recent Users"
              items={stats?.recentUsers ?? []}
              icon={UsersIcon}
              type="user"
            />
            <RecentActivityCard
              title="Recent Files"
              items={stats?.recentFiles ?? []}
              icon={FileTextIcon}
              type="file"
            />
          </div>
        </div>
      </div>

      {/* Version Info */}
      <div className="mt-8 pt-4 border-t border-border text-center text-sm text-muted-foreground">
        GearLens v{appVersion?.full || 'unknown'}
      </div>
    </div>
  )
}
